import React, { createContext, useReducer } from "react"

import { Toast } from "primereact/toast/toast"

import { Action, ActionType, AppContextType, State } from "./interfacesAndTypes"

const initialState: State = {
  client_id: "",
  toast: null,
  showContactFormWindow: false,
  showChatWindow: false,
  showSideMenu: false,
  chatAiName: "timai",
  showAcceleratorFormWindow: false,
  showHamburgermenu: false,
  homepageMessage: "",
  sendHomepageChatMessage: false,
  showNewChatWindow: false,
  isMobile: false,
  isTab: false,
}

const reducer = (state: State, action: Action<ActionType> | any): State => {
  // const { contextStateKey, payload } = (action?.payload as 'contextStateKey' | 'payload') ?? {};

  switch (action.type) {
    case "SET_TOAST_REF": {
      return { ...state, toast: action.payload as Toast }
    }

    case "SET_CLIENT_ID": {
      return {
        ...state,
        client_id: action.payload.client_id as string,
      }
    }

    case "SET_SHOW_CONTACT_FORM_WINDOW": {
      return {
        ...state,
        showContactFormWindow: action.payload,
      }
    }

    case "SET_SHOW_CHAT_WINDOW": {
      return {
        ...state,
        showChatWindow: action.payload,
        // showChatWindow: false,
      }
    }

    case "SET_SHOW_SIDE_MENU": {
      return {
        ...state,
        showSideMenu: action.payload,
      }
    }

    case "SET_CHAT_AI_NAME": {
      return {
        ...state,
        chatAiName: action.payload,
      }
    }

    case "SET_SHOW_ACCELERATOR_FORM_WINDOW": {
      return {
        ...state,
        showAcceleratorFormWindow: action.payload,
      }
    }

    case "SET_SHOW_HAMBURGERMENU": {
      return {
        ...state,
        showHamburgermenu: action.payload,
      }
    }

    case "SET_SEND_HOMEPAGE_CHAT_MESSAGE": {
      return {
        ...state,
        sendHomepageChatMessage: action.payload,
      }
    }

    case "SET_HOMEPAGE_CHAT_MESSAGE": {
      return {
        ...state,
        homepageMessage: action.payload,
      }
    }

    case "SET_SHOW_NEW_CHAT_WINDOW": {
      sessionStorage.setItem("showChatInterface", action.payload)
      return {
        ...state,
        showNewChatWindow: action.payload,
        // showNewChatWindow: false,
      }
    }

    case "SET_IS_Mobile": {
      return {
        ...state,
        isMobile: action.payload,
      }
    }
    case "SET_IS_Tab": {
      return {
        ...state,
        isTab: action.payload,
      }
    }

    default:
      return state
  }
}

const AppContext = createContext<AppContextType>({
  state: initialState,
  dispatch: () => null,
  setClientId: () => null,
  showToast: () => null,
  setShowContactFormWindow: () => null,
  setShowChatWindow: () => null,
  setShowSideMenu: () => null,
  setChatAiName: () => null,
  setShowAcceleratorFormWindow: () => null,
  setShowHamburgermenu: () => null,
  setHomepageMessage: () => null,
  setSendHomepageChatMessage: () => null,
  setShowNewChatWindow: () => null,
  setIsMobile: () => null,
  setIsTab: () => null,
})

const AppContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const setClientId = (client_id: string) => {
    dispatch({
      type: "SET_CLIENT_ID",
      payload: { client_id },
    })
  }

  const showToast = (
    severity: "success" | "info" | "warn" | "error" | undefined,
    summary: "Success" | "Info" | "Warning" | "Error",
    detail: string,
    life?: number
  ) => {
    // state.toast?.clear();
    state.toast?.show({ severity, summary, detail, life })
  }

  const setShowContactFormWindow = (showContactFormWindow: boolean) => {
    dispatch({
      type: "SET_SHOW_CONTACT_FORM_WINDOW",
      payload: showContactFormWindow,
    })
  }

  const setHomepageMessage = (homepageMessage: string) => {
    dispatch({
      type: "SET_HOMEPAGE_CHAT_MESSAGE",
      payload: homepageMessage,
    })
  }

  const setSendHomepageChatMessage = (sendHomepageChatMessage: boolean) => {
    dispatch({
      type: "SET_SEND_HOMEPAGE_CHAT_MESSAGE",
      payload: sendHomepageChatMessage,
    })
  }

  const setShowNewChatWindow = (showNewChatWindow: boolean) => {
    dispatch({ type: "SET_SHOW_NEW_CHAT_WINDOW", payload: showNewChatWindow })
  }
  const setShowChatWindow = (showChatWindow: boolean) => {
    dispatch({ type: "SET_SHOW_NEW_CHAT_WINDOW", payload: showChatWindow })
  }

  const setShowSideMenu = (showSideMenu: boolean) => {
    dispatch({ type: "SET_SHOW_SIDE_MENU", payload: showSideMenu })
  }

  const setChatAiName = (chatAiName: string) => {
    dispatch({ type: "SET_CHAT_AI_NAME", payload: chatAiName })
  }

  const setShowAcceleratorFormWindow = (showAcceleratorFormWindow: boolean) => {
    dispatch({
      type: "SET_SHOW_ACCELERATOR_FORM_WINDOW",
      payload: showAcceleratorFormWindow,
    })
  }

  const setShowHamburgermenu = (showHamburgermenu: boolean) => {
    dispatch({ type: "SET_SHOW_HAMBURGERMENU", payload: showHamburgermenu })
  }
  const setIsMobile = (isMobile: boolean) => {
    dispatch({ type: "SET_IS_Mobile", payload: isMobile })
  }
  const setIsTab = (isTab: boolean) => {
    dispatch({ type: "SET_IS_Tab", payload: isTab })
  }

  const contextValue: AppContextType = {
    state,
    dispatch,
    setClientId,
    showToast,
    setShowContactFormWindow,
    setShowChatWindow,
    setShowSideMenu,
    setChatAiName,
    setShowAcceleratorFormWindow,
    setShowHamburgermenu,
    setHomepageMessage,
    setSendHomepageChatMessage,
    setShowNewChatWindow,
    setIsMobile,
    setIsTab,
  }

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  )
}

const useAppContext = () => {
  const context = React.useContext<AppContextType>(AppContext)

  if (context === undefined) {
    throw new Error("useAppContext must be used within a AppContextProvider")
  }

  return context
}

export { AppContext, AppContextProvider, useAppContext }
